import axios from 'axios'
import _ from 'lodash'

import ApiService from '../../../helpers/apiservice'
import * as Constants from '../../../helpers/constants'
import { DAILY_DAYS } from './constants'

/* Account monitoring data-access seam.
 *
 * sfcloud proxies these four routes to cocloud under api/v1/admin/email_accounts/* — see
 * .claude/specs/account-monitoring-sfcloud-proxy-spec.md. The endpoints take no client, so
 * unlike every other page here this bypasses ApiHelper(client, apiName): that layer
 * dispatches on CLIENTS[client].SERVICE and there is no client to dispatch on.
 */
const isSuccess = (response) => response && response.status === Constants.CO_API.SUCCESS

// ajax() rejects with the raw axios error, whose `message` is "Request failed with status
// code 400" — useless in a UI. Dig the API's own message out of the response body instead.
const errorMessage = (err, fallback) =>
  _.get(err, 'response.data.error.message') ||
  _.get(err, 'response.data.message') ||
  _.get(err, 'error.message') ||
  (err && err.message) ||
  fallback

// A cancellation must leave this layer as an axios CanceledError. Wrapping it in a plain
// Error strips the `__CANCEL__` marker that axios.isCancel() reads in the component, and
// an aborted request would then render as a real failure reading literally "canceled".
const rethrowIfCancelled = (err) => {
  if (axios.isCancel(err)) throw err
}

// Whole-table snapshot: one item per connected mailbox, plus the degraded/blocked counters.
// Per-row counts may be null — never coerce them to 0; that distinction is the whole point.
export const fetchAccountStats = (signal = null) =>
  ApiService.getEmailAccountStats(signal)
    .then((response) => {
      if (!isSuccess(response)) {
        throw new Error((response && response.message) || 'Failed to load mailbox stats.')
      }
      const data = response.data || {}
      return {
        items: data.items || [],
        total: data.total || 0,
        totals: data.totals || {},
        degraded: data.degraded || 0,
        blocked: data.blocked || 0,
      }
    })
    .catch((err) => {
      rethrowIfCancelled(err)
      throw new Error(errorMessage(err, 'Failed to load mailbox stats.'))
    })

// Per-account daily breakdown, newest first. Item 0 is the trailing 24h and items 1+ are
// whole UTC calendar days — an asymmetry the strip's caption has to explain to the user.
export const fetchAccountDaily = (account, days = DAILY_DAYS, signal = null) =>
  ApiService.getEmailAccountDaily({ account, days }, signal)
    .then((response) => {
      if (!isSuccess(response)) {
        throw new Error((response && response.message) || 'Failed to load the daily breakdown.')
      }
      const data = response.data || {}
      return {
        account: data.account || account,
        path: data.path || '',
        timezone: data.timezone || 'UTC',
        items: data.items || [],
      }
    })
    .catch((err) => {
      rethrowIfCancelled(err)
      throw new Error(errorMessage(err, 'Failed to load the daily breakdown.'))
    })

// Sends REAL outbound mail from a customer-facing domain. Only ever call this from the
// confirmed second click of the two-step arm.
//
// Takes no abort signal on purpose: once EmailEngine has accepted the message, cancelling
// the request would hide the outcome, not un-send the mail. Success here means "accepted
// for delivery", not "delivered".
export const sendTestEmail = (account) =>
  ApiService.postEmailAccountTestSend({ account })
    .then((response) => {
      if (!isSuccess(response)) {
        throw new Error((response && response.message) || 'Test send failed.')
      }
      return response.data || {}
    })
    .catch((err) => {
      throw new Error(errorMessage(err, 'Test send failed.'))
    })
